import { Config } from '~admin/config';
import { EventDatetimeDetailsFormData } from '~admin/view/components/details/event-details/event-datetime-details.component';
import { EventFeeFormData } from '~admin/view/components/details/event-details/event-fee-details.component';
import { EventLocationFormData } from '~admin/view/components/details/event-details/event-location-details';
import { EventMediaFormData } from '~admin/view/components/details/event-details/event-media-details.component';
import { EventOptionsFormData } from '~admin/view/components/details/event-details/event-options-details.component';
import { EventRestrictionItemFormData } from '~admin/view/components/details/event-details/event-restrictions/event-restriction-item-fields.component';
import { EventTimelineGroupFormData } from '~admin/view/components/details/event-details/event-timeline-details/event-timeline-group.component';
import { EventTimelineItemFormData } from '~admin/view/components/details/event-details/event-timeline-details/event-timeline-item-fields.component';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { MarketplaceFormData } from '~admin/view/components/details/marketplace-details/marketplace-details-fields.component';
import { OfferGroupFormData } from '~admin/view/components/details/offer-group-details/offer-group-fields.component';
import { SuggestedIncrementsFormData } from '~admin/view/components/details/offer-group-details/offer-group-suggested-increments.component';
import { EventSponsorGroupFormData } from '~admin/view/components/details/sponsor-details/sponsor-details-group.component';
import { TranslationFormData } from '~admin/view/components/ui/form/fields/field-translation.component';
import { MediaUploadFormData } from '~admin/view/components/ui/form/fields/media/media-overlay/media-picker-upload.component';
import { AccessibilityType } from '~client/model/accessibility-type';
import { Currency } from '~client/model/currency';
import { SeatConfigurationType } from '~client/model/seat-configuration-type';
import { datetime } from '~common/helper/datetime';
import { Language } from '~common/i18n';
import { EventMerchandiseRestrictionsFormData } from '~admin/view/components/details/event-details/event-restrictions/event-merchandise-restrictions.component';
import { OfferGroupPriceFormData } from '~admin/view/components/details/offer-group-details/offer-group-prices.component';

/**
 * Returns initial values for a translation field
 * @param minutes
 */
const getBaseDate = (minutes = 0): string =>
  datetime.now().add(minutes, 'minute').format(Config.dateTimeFormats.dateTimeField);

/**
 * Returns initial values for a translation field
 * @param language
 */
export const getTranslationInitialValues = (language: Language): TranslationFormData<any> => ({
  language,
  value: null,
});

/**
 * Returns initial values for a media upload form
 */
export const getMediaUploadInitialValues = (): MediaUploadFormData => ({
  file: [getTranslationInitialValues(Language.ENGLISH)],
  folderName: null,
  description: null,
  width: null,
  height: null,
});

/**
 * Returns initial values for location details
 */
const getEventLocationInitialValues = (): EventLocationFormData => ({
  eventLocationId: Config.isDebugBuild ? 34 : null,
  seatConfigurationId: null,
  eventAccessibilityType: AccessibilityType.STREAMING,
  seatMapMedia: null,
});

/**
 * Returns initial values for event media details
 */
const getEventMediaInitialValues = (): EventMediaFormData => ({
  avatarMedia: null,
  defaultMedia: null,
  emailMedia: null,
  landscapeVideo: null,
  portraitVideo: null,
});

/**
 * Returns initial values for event datetime details
 */
const getEventDatetimeInitialValues = (): EventDatetimeDetailsFormData => ({
  eventDateTimeStart: getBaseDate(15),
  eventDateTimeEnd: getBaseDate(75),
  discoverDateStart: getBaseDate(15),
  discoverDateEnd: getBaseDate(75),
  doorsOpenDate: getBaseDate(15),
});

/**
 * Returns initial values for event fee details
 */
const getEventFeeInitialValues = (): EventFeeFormData => ({
  flatFee: 0,
  resaleFlatFee: 0,
  creditCardFeeRate: 0,
  sellerCreditCardFeeRate: 0,
  profitFeeRate: 0,
  taxType: null,
  includedInBase: false,
});

/**
 * Returns initial values for event's additional options details
 */
const getEventOptionsInitialValues = (): EventOptionsFormData => ({
  ticketLimit: 1,
  currency: Currency.USD,
  vanityName: Config.isDebugBuild ? datetime.now().unix().toString() : null,
  externalNotificationSubscriptionsId: null,
  externalNotificationSubscriptionsText: [getTranslationInitialValues(Language.ENGLISH)],
});

/**
 * Returns initial values for event timeline (schedule) item
 * @param rank
 */
export const getEventTimelineItemInitialValues = (rank: number): EventTimelineItemFormData => ({
  title: [getTranslationInitialValues(Language.ENGLISH)],
  description: [getTranslationInitialValues(Language.ENGLISH)],
  rank,
});

/**
 * Returns initial values for event timeline (schedule) group
 */
export const getEventTimelineGroupInitialValues = (): EventTimelineGroupFormData => ({
  title: [getTranslationInitialValues(Language.ENGLISH)],
  eventTimelineItems: [getEventTimelineItemInitialValues(1)],
});

export const getEventRestrictionInitialValues = (): EventRestrictionItemFormData => ({
  description: [getTranslationInitialValues(Language.ENGLISH)],
  url: [getTranslationInitialValues(Language.ENGLISH)],
});

const getEventMerchandiseRestrictionInitialValues = (): EventMerchandiseRestrictionsFormData => ({
  merchText: [getTranslationInitialValues(Language.ENGLISH)],
  merchButtonText: [getTranslationInitialValues(Language.ENGLISH)],
  merchButtonUrl: [getTranslationInitialValues(Language.ENGLISH)],
});

/**
 * Returns initial values for event sponsor group
 */
export const getEventSponsorGroupInitialValues = (): EventSponsorGroupFormData => ({
  height: null,
  sponsors: [],
});

/**
 * Returns initial values for an item of suggested increments
 */
export const getSuggestedIncrementsInitialValues = (): SuggestedIncrementsFormData => ({
  title: [getTranslationInitialValues(Language.ENGLISH)],
  value: null,
});

/**
 * Returns initial values for an offer group price
 */
export const getOfferGroupPriceInitialValues = (): OfferGroupPriceFormData => ({
  price: null,
  startDateTime: null,
  endDateTime: null,
});

/**
 * Returns initial values for a single offer group
 */
export const getOfferGroupInitialValues = (): OfferGroupFormData => ({
  name: [getTranslationInitialValues(Language.ENGLISH)],
  prices: [getOfferGroupPriceInitialValues()],
  capacityDefaultString: [getTranslationInitialValues(Language.ENGLISH)],
  additionalDetails: [getTranslationInitialValues(Language.ENGLISH)],
  seatConfigurationType: SeatConfigurationType.GENERAL_ADMISSION,
  sections: [null], //null needed to render one section-dropdown without any selected value
  rowRange: '',
  seatRange: '',
  suggestedIncrements: [],
});

/**
 * Returns initial values for a single marketplace
 */
export const getMarketplaceInitialValues = (): MarketplaceFormData => ({
  type: null,
  ticketLimit: null,
  startDateTime: null,
  endDateTime: null,
  rules: [getTranslationInitialValues(Language.ENGLISH)],
  capacity: [getTranslationInitialValues(Language.ENGLISH)],
  lowCapacityPercentageThreshold: null,
  offerGroups: [getOfferGroupInitialValues()],
});

/**
 * Returns initial values for create event form
 */
export const getCreateEventInitialValues = (): EventFormData => ({
  clientId: Config.isDebugBuild ? 2 : null,
  eventName: [getTranslationInitialValues(Language.ENGLISH)],
  eventDescription: [getTranslationInitialValues(Language.ENGLISH)],
  eventLanguage: Language.ENGLISH,
  ...getEventLocationInitialValues(),
  ...getEventMediaInitialValues(),
  ...getEventDatetimeInitialValues(),
  ...getEventFeeInitialValues(),
  ...getEventOptionsInitialValues(),
  ...getEventMerchandiseRestrictionInitialValues(),
  marketplaces: [getMarketplaceInitialValues()],
});



// WEBPACK FOOTER //
// ./view/components/create-event/helper/create-event-initial-values.helper.ts